import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useLocalStorage } from './useLocalStorage';

/**
 * Returns the number of new notifications (zaps + reactions + mentions) since
 * the user last visited the notifications page.
 */
export function useNotificationCount() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  // Persist the last-seen timestamp so the badge persists across reloads
  const [lastSeen, setLastSeen] = useLocalStorage<number>(
    'goblinbox:notifications:last-seen',
    0,
  );

  const query = useQuery({
    queryKey: ['nostr', 'notification-count', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user) return 0;

      const since = lastSeen || Math.floor(Date.now() / 1000) - 7 * 24 * 60 * 60; // last 7 days

      const events = await nostr.query(
        [
          {
            // zap receipts targeting the user
            kinds: [9735],
            '#p': [user.pubkey],
            since,
            limit: 50,
          },
          {
            // reactions to the user's content
            kinds: [7],
            '#p': [user.pubkey],
            since,
            limit: 50,
          },
          {
            // mentions / replies
            kinds: [1],
            '#p': [user.pubkey],
            since,
            limit: 50,
          },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );

      return events.length;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000, // poll every 2 minutes
  });

  const markAllRead = () => {
    setLastSeen(Math.floor(Date.now() / 1000));
  };

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    markAllRead,
    lastSeen,
  };
}
